import { create } from "zustand"

/**
 * Per-browser "tender admin mode" switch.
 *
 * Tender admins are NOT elevated by default: membership in the `tender_admins`
 * policy only decides whether this toggle is offered. Turning it on opts the
 * admin into elevated access (read/edit any case); off gives the normal,
 * involvement-based experience. The choice is remembered in localStorage —
 * the same way the tender landing-page filters are (see `FILTER_STORAGE_KEYS`
 * in tender-landing-page.tsx) — and defaults to OFF.
 *
 * Enforcement is server-side: the state rides along on tender requests in the
 * `X-Tender-Admin-Mode` header (see the Axios interceptor in lib/api-client.ts
 * and `getTenderHeaders` in api/tender-api.ts), and the backend only honours it
 * for real tender admins (`tender_admin_mode_active`). The header can only drop
 * privileges, so persisting/sending it from the client is safe.
 */

export const TENDER_ADMIN_MODE_HEADER = "X-Tender-Admin-Mode"
const STORAGE_KEY = "tender-admin-mode"

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "on"
  } catch {
    return false
  }
}

function writeStored(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? "on" : "off")
  } catch {
    // ignore (e.g. storage disabled / private mode)
  }
}

type TenderAdminModeStore = {
  enabled: boolean
  setEnabled: (enabled: boolean) => void
  toggle: () => void
}

export const useTenderAdminModeStore = create<TenderAdminModeStore>()((set, get) => ({
  enabled: readStored(),
  setEnabled(enabled) {
    writeStored(enabled)
    set({ enabled })
  },
  toggle() {
    get().setEnabled(!get().enabled)
  },
}))

/** Non-React read for the Axios interceptor / fetch header builder. */
export function isTenderAdminModeEnabled(): boolean {
  return useTenderAdminModeStore.getState().enabled
}

/** The header value to send on tender requests ("on" | "off"). */
export function tenderAdminModeHeaderValue(): "on" | "off" {
  return isTenderAdminModeEnabled() ? "on" : "off"
}
